// views/Forms/Listings/Listing/OrderDetailsCard.js
// ─────────────────────────────────────────────────────────────────────────────
// The platform admin's view of a shop order, and the screen COD orders are
// worked from.
//
// REBUILT 27 Sep 2026, after order 6ab87a8c07d7ea2ae7b1199b (Mato Maha, one
// quilt, COD) sat in PROCESSING for seven hours because nobody could act on it
// from here. Three faults, each on its own enough to strand an order:
//
//   1. NOTHING COULD BE SELECTED. Assignment is per item, and the radio that
//      sets the item was the seventh of nine columns in a table wider than the
//      dark panel holding it. It rendered past the right edge of the card, and
//      macOS draws no scrollbar until you scroll, so there was no hint it
//      existed. Choosing a rider and pressing Assign answered "Please select an
//      item." — about a control that was not on screen.
//
//   2. THE ASSIGN PANEL VANISHED ON ASSIGNED ORDERS. `(a && b) || (c && d &&
//      <div/>)` — `&&` binds tighter than `||`, so an ASSIGNED order
//      short-circuited to the boolean `true`, which React renders as nothing.
//      The second item of a two-item order could never be assigned, and no
//      order could be moved to a different rider.
//
//   3. EVERY BUTTON LOOKED DISABLED. `variant="contained" color="primary"` are
//      MUI props passed to a react-bootstrap Button, which ignores them, and a
//      permanent `disabledButton` grey class sat on top with a `data-disabled`
//      attribute that only styled the cursor. Buttons were grey when they
//      worked and clickable when they looked dead.
//
// The money lines were wrong too: "Shipping Fees" read the deprecated flat zone
// fee, so a fee-engine order showed 70,000 and 76,500 with a blank line
// between them. See utilities/orderSummary.js.
//
// The screen now draws in the shared .rod-* light theme every other
// order-details page uses (components/Orders/orderDetailsTheme.css), takes its
// items table, money lines and rider panel from components/Orders/ so the
// vendor's view of the same order cannot drift from it, and gives the items
// table the full page width — which is what makes the clipping unrepeatable.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useRef, useState } from "react";
import { FormControlLabel, Switch } from "@material-ui/core";
import moment from "moment";
import { Container, Modal } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import {
  assignOrderToDeliveryAgent,
  getOrderDetailsToProcess,
  markOrderAsPaid,
  updateDeliveryStatus,
  updateOverallOrderDeliveryStatus,
} from "../../../../actions/webOrderActions";
import { getListing } from "../../../../actions/listingsActions.js";
import { openReceiptForPrint } from "../../../../utilities/openReceipt";
import VendorHandoffActions from "../../../../components/Orders/VendorHandoffActions";
import ContactAction from "../../../../components/Orders/ContactAction";
import { RESET_UPDATE_DELIVERY_STATUS_COMPLETE } from "constants/webOrderConstants.js";

import DeliveryAdjustmentPanel from "../../../../components/DeliveryAdjustmentPanel";
import DeliveryScheduleBadge from "../../../../components/DeliveryScheduleBadge";
import Loading from "../../../../components/Loading";
import DropoffMap from "../../../../components/Maps/DropoffMap";
import OrderTimeline from "../../../../components/OrderTimeline";
import AssignRiderPanel from "../../../../components/Orders/AssignRiderPanel";
import OrderItemsTable, {
  isItemSelectable,
  itemKeyOf,
} from "../../../../components/Orders/OrderItemsTable";
import OrderMoneyPanel from "../../../../components/Orders/OrderMoneyPanel";
import { Fact, Panel } from "../../../../components/Orders/OrderPanel";
import OrderNextStep from "../../../../components/Orders/OrderNextStep";
import OrderStepTracker from "../../../../components/Orders/OrderStepTracker";
import {
  DeliveryStatusBadge,
  PaymentBadge,
} from "../../../../components/OrderStatusBadges";
import useLusukuRiders from "../../../../hooks/useLusukuRiders";
import useOrderLiveUpdates from "../../../../hooks/useOrderLiveUpdates";
import {
  AUDIENCE,
  canVendorDispatch,
  canVendorMarkDelivered,
} from "../../../../utilities/orderDeliverySteps";
import { getShippingAddressText } from "../../../../utilities/orderPresentation";
import { resolveOrderVendors } from "../../../../utilities/orderTableSorting";
import { isRiderAssignmentLocked } from "../../../../utilities/orderRiderLock";
import { getUserFromLocalStorage } from "../../../../utilities/user.js";
import "../../../../components/Orders/orderDetailsTheme.css";

const AUDIT_ENABLED = process.env.REACT_APP_FEATURE_AUDIT_TRAIL === "true";

const OrderDetailsCard = ({ match }) => {
  const user = getUserFromLocalStorage();
  const paramsOrderId = match.params.id;

  const history = useHistory();
  const dispatch = useDispatch();

  const [riderId, setRiderId] = useState("");
  const [itemId, setItemId] = useState("");
  const [showCancelModal, setShowCancelModal] = useState(false);

  const { riders, loading: loadingRiders, error: ridersError } =
    useLusukuRiders();

  const mobileOrderDeliver = useSelector((state) => state.mobileOrderDeliver);
  const { loading: loadingDeliver } = mobileOrderDeliver;

  const orderDetails = useSelector((state) => state.orderDetails);
  const { order, loading, error, isUpdateDeliveryStatusComplete } = orderDetails;

  // Live order news (a rider's pickup, a 3PL delivery, a decline) through the
  // one shared feed; see hooks/useOrderLiveUpdates.js.
  useOrderLiveUpdates(paramsOrderId, {
    onChange: (fresh) => {
      if (fresh?.delivery_status === "PROCESSING" && !fresh?.riderId)
        setRiderId("");
    },
  });

  useEffect(() => {
    dispatch(getOrderDetailsToProcess(paramsOrderId));

    if (isUpdateDeliveryStatusComplete) {
      dispatch(getOrderDetailsToProcess(paramsOrderId));
      dispatch({ type: RESET_UPDATE_DELIVERY_STATUS_COMPLETE });
    }
  }, [isUpdateDeliveryStatusComplete, paramsOrderId]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  // ── Brand orders belong on the brand order screen ────────────────────────
  // This generic card can only drive per-ITEM delivery status. It never touches
  // `brandFlowStage`, so a brand order worked here ends up with its items
  // DELIVERED while the brand stage is still stuck on AWAITING_VENDOR_FULFILMENT
  // — the two desync silently and the vendor's own screen shows an order that
  // never progressed. It also has no quote-stage stepper, no 3PL send/recall and
  // no re-assign, which is why staff could not process a brand order from here.
  //
  // Every order list pushes to /admin/orderDetails/:id regardless of type, so
  // redirecting at the destination catches every entry point — list rows, deep
  // links and bookmarks alike — instead of patching each caller. The backend
  // already authorises platform admins on all the brand actions
  // (brandOrderStageController's isAdmin bypass, canUserManageOrder's
  // isPlatformAdminUser short-circuit), so an admin landing there is fully able
  // to act; only the UI was missing.
  //
  // `replace`, not `push`: a push would leave this card in history and Back
  // would bounce the user straight into the redirect again.
  const brandRedirectedRef = useRef(false);
  useEffect(() => {
    if (brandRedirectedRef.current) return;
    if (!order?._id || !order?.isBrandOrder) return;
    brandRedirectedRef.current = true;
    history.replace(`/admin/brandVendorOrderDetails/${order._id}`);
  }, [order?._id, order?.isBrandOrder, history]);

  // ── Ticket orders belong on the ticket order page ────────────────────────
  // Same reasoning as the brand redirect above. A ticket order has no delivery
  // leg: this card drew it as one (every delivery rung ticked, the walk-in
  // "Fulfilled at the counter" banner, its line PROCESSING after the ticket
  // was issued, rider and dispatch controls). The ticket page shows its own
  // cycle, reservation to gate (16 Oct 2026). Deep links, bookmarks and the
  // new-sale notification all land here first.
  const ticketRedirectedRef = useRef(false);
  const isTicketOrder = order?.orderKind === "TICKET";
  useEffect(() => {
    if (ticketRedirectedRef.current) return;
    if (!order?._id || !isTicketOrder) return;
    ticketRedirectedRef.current = true;
    history.replace(`/admin/ticketOrderDetails/${order._id}`);
  }, [order?._id, isTicketOrder, history]);

  /* A selection that no longer refers to a line of THIS order can only produce
     "Selected item not found" at the far end of a rider assignment. Clearing it
     when the order changes underneath us keeps the button honest. */
  useEffect(() => {
    if (!itemId) return;
    const stillHere = (order?.orderItems || []).some(
      (item) => itemKeyOf(item) === itemId
    );
    if (!stillHere) setItemId("");
  }, [order?.orderItems, itemId]);

  const selectableItems = (order?.orderItems || []).filter(isItemSelectable);
  const selectedItem = (order?.orderItems || []).find(
    (item) => itemKeyOf(item) === itemId
  );

  const isStaff =
    user?.result?.userType === "LUSUKU STAFF" ||
    user?.result?.user_role === "SUPERUSER";

  const handleMarkAsPaid = () => {
    if (!isStaff) {
      toast.warn("You are not authorised to mark an order as paid.");
      return;
    }
    dispatch(markOrderAsPaid(order._id));
  };

  const assignOrderToRider = () => {
    if (!itemId) {
      toast.warn("Select the item you are handing over first.");
      return;
    }
    if (!riderId) {
      toast.warn("Choose a rider.");
      return;
    }
    if (!selectedItem) {
      toast.error("That item is no longer on this order. Select it again.");
      setItemId("");
      return;
    }
    dispatch(
      assignOrderToDeliveryAgent(order._id, riderId, itemId, "ASSIGNED")
    );
    setItemId("");
  };

  const moveSelectedItemTo = (delivery_status, verb) => {
    if (!itemId) {
      toast.warn(`Select the item you are marking as ${verb}.`);
      return;
    }
    dispatch(updateDeliveryStatus(order._id, itemId, delivery_status));
    setItemId("");
  };

  const handleCancelOrder = () => {
    /* The whole order, resolved on the server. This used to post one entry per
       line id the client could work out, and a line whose listing had been
       deleted had no id, so the order answered "cannot be cancelled from
       here" to the person whose job it is to cancel it. The server rejects
       every line itself now (updateOverallOrderDeliveryStatus, applyToAll). */
    dispatch(
      updateOverallOrderDeliveryStatus(order._id, {
        applyToAll: { delivery_status: "REJECTED" },
      })
    );
    setShowCancelModal(false);
  };

  // The receipt document (seller, TIN, customer, payment, lines, URA
  // markings when issued), opened for printing. Replaces the older PDF
  // download, which this screen had disabled.
  const generateReceiptByAgent = () => openReceiptForPrint(order?._id);

  const items = order?.orderItems || [];
  const allItemsClosed =
    items.length > 0 &&
    items.every((item) =>
      ["DELIVERED", "REJECTED"].includes(item.delivery_status)
    );

  const canCancel =
    order?.delivery_status === "PROCESSING" && !order?.isPaid && !allItemsClosed;

  const paymentRows = order?.paymentResult
    ? [
        order.paymentResult.narration,
        order.paymentResult.transactionId &&
          `Transaction ID: ${order.paymentResult.transactionId}`,
        order.paymentResult.amount &&
          `Charged: ${order.paymentResult.amount} ${
            order.paymentResult.currency || ""
          }`.trim(),
        order.paymentResult.flw_ref,
        order.paymentResult.app_fee && `App fee: ${order.paymentResult.app_fee}`,
        order.paymentResult.card?.first_6digits &&
        order.paymentResult.card?.last_4digits
          ? `${order.paymentResult.card.first_6digits}*******${order.paymentResult.card.last_4digits}`
          : order.paymentResult.auth_model &&
            `Auth model: ${order.paymentResult.auth_model}`,
        order.paymentResult.update_time &&
          moment(order.paymentResult.update_time).format("lll"),
      ].filter(Boolean)
    : [];

  if (loading) {
    return (
      <Container fluid>
        <Loading size="page" label="Loading this order..." />
      </Container>
    );
  }

  const latLng = order?.shippingAddress?.latLng;
  /* Who is selling. Standard on every order: Lusuku is multivendor, and this
     page used to name the customer, the rider and the agent but never the
     seller the order was placed with. One entry per distinct vendor, so a
     basket from two shops names both. */
  const vendors = order ? resolveOrderVendors(order) : [];
  const deliveryZone =
    order?.shippingAddress?.selectedVillage?.village ||
    order?.shippingAddress?.locationName?.vicinity ||
    order?.shippingAddress?.description;

  return (
    <Container fluid>
      <div className="rod-canvas">
        {/* ── Header ───────────────────────────────────────────────────── */}
        <div className="rod-topbar">
          <div className="rod-topbar-title">
            <h4>{order?.client_id?.name || "Customer"}</h4>
            <span>
              Order {order?.order_number || order?._id}
              {order?.createdAt &&
                ` · ${moment(order.createdAt).format("MMM D, YYYY · h:mm a")}`}
            </span>
          </div>
          <div
            className="rod-topbar-actions"
            style={{ display: "flex", gap: 8, alignItems: "center" }}
          >
            <DeliveryStatusBadge status={order?.delivery_status} />
            <PaymentBadge order={order} />
            {order && <DeliveryScheduleBadge order={order} />}
            <button
              type="button"
              className="lk-action-btn lk-action-btn--lg lk-action-btn--quiet"
              onClick={() => history.goBack()}
            >
              Back
            </button>
          </div>
        </div>

        {/* What happens next, in the same words every other order screen uses */}
        {order && (
          <>
            {/* Where the order IS, then what to do about it. The banner alone
                is an instruction with no map. */}
            <OrderStepTracker order={order} audience={AUDIENCE.ADMIN} />
            <OrderNextStep
              order={order}
              audience={AUDIENCE.ADMIN}
              style={{ marginBottom: 14 }}
            />
          </>
        )}

        <div className="rod-main-grid">
          {/* ── Customer and delivery ─────────────────────────────────── */}
          <Panel title="Customer and delivery">
            <div className="rod-facts">
              <Fact
                label={vendors.length > 1 ? "Sold by (vendors)" : "Sold by"}
                wide
                value={
                  vendors.length ? (
                    <span className="rod-vendor-list">
                      {vendors.map((v) => (
                        <span
                          key={`${v.label}|${v.hasVendorRef}`}
                          className={`rod-vendor${v.resolved ? "" : " rod-vendor--unresolved"}`}
                          title={v.title || undefined}
                        >
                          {v.label}
                        </span>
                      ))}
                    </span>
                  ) : null
                }
              />
              <Fact label="Customer" value={order?.client_id?.name} />
              <Fact
                label="Phone"
                value={
                  order?.shippingAddress?.customerNumber ? (
                    <ContactAction
                      label="Customer"
                      name={order?.client_id?.name}
                      phone={order?.shippingAddress?.customerNumber}
                      compact
                    />
                  ) : null
                }
              />
              <Fact label="Agent" value={order?.agent_id?.name} />
              <Fact label="Delivery zone" value={deliveryZone} />
              <Fact
                label="Address"
                wide
                value={getShippingAddressText(order?.shippingAddress, "")}
              />
              <Fact
                label="Dropoff pin"
                wide
                value={
                  latLng?.lat && latLng?.lng ? (
                    /* The place name, the precision chip, share-to-rider and
                       the collapsible map all live in one component so the
                       vendor's view of the same order cannot drift from this
                       one. See components/Maps/DropoffMap.jsx. */
                    <DropoffMap
                      lat={latLng.lat}
                      lng={latLng.lng}
                      address={getShippingAddressText(
                        order?.shippingAddress,
                        ""
                      )}
                      shippingAddress={order?.shippingAddress}
                      orderRef={order?.order_number || order?._id}
                      rider={order?.riderId}
                      country={order?.country}
                    />
                  ) : null
                }
              />
              <Fact
                label="Note from customer"
                wide
                value={order?.message || null}
              />
              <Fact
                label="Placed"
                value={order?.createdAt && moment(order.createdAt).fromNow()}
              />
              <Fact
                label="Delivered"
                value={
                  order?.deliveredAt && moment(order.deliveredAt).fromNow()
                }
              />
              <Fact
                label="Rider"
                value={
                  order?.riderId?.firstName
                    ? `${order.riderId.firstName} ${
                        order.riderId.lastName || ""
                      }`.trim()
                    : null
                }
              />
            </div>
          </Panel>

          {/* ── Payment ───────────────────────────────────────────────── */}
          <Panel title="Payment">
            <OrderMoneyPanel order={order} />

            <div
              style={{
                marginTop: 14,
                paddingTop: 12,
                borderTop: "1px solid #f1f5f9",
              }}
            >
              <div className="rod-facts">
                <Fact label="Method" value={order?.paymentMethod} />
                <Fact
                  label="Paid on"
                  value={
                    order?.isPaid && order?.paidAt
                      ? moment(order.paidAt).utcOffset("+03:00").format("lll")
                      : null
                  }
                />
              </div>

              <div
                className="rod-actions"
                style={{ marginTop: 10, justifyContent: "space-between" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={order?.isPaid || false}
                      onChange={handleMarkAsPaid}
                      color="primary"
                      size="small"
                    />
                  }
                  label={
                    <span style={{ fontSize: "0.82rem" }}>
                      {order?.isPaid ? "Paid" : "Mark as paid"}
                    </span>
                  }
                  disabled={
                    order?.isPaid ||
                    order?.delivery_status === "REJECTED" ||
                    !items.some((item) => item.delivery_status === "DELIVERED")
                  }
                />
                <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <VendorHandoffActions orderId={order?._id} />
                  <button
                    type="button"
                    className="lk-action-btn lk-action-btn--lg"
                    onClick={generateReceiptByAgent}
                    title="Open the receipt document"
                  >
                    Receipt
                  </button>
                </div>
              </div>

              {!order?.isPaid &&
                !items.some(
                  (item) => item.delivery_status === "DELIVERED"
                ) && (
                  <div className="rod-hint" style={{ marginTop: 6 }}>
                    An order can be marked paid once at least one item has been
                    delivered.
                  </div>
                )}

              {paymentRows.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  {paymentRows.map((row, i) => (
                    <div key={i} className="rod-hint" style={{ padding: "2px 0" }}>
                      {row}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Panel>
        </div>

        {/* ── Items — full page width, so nothing can be clipped ────────── */}
        <Panel
          title={`Items (${items.length})`}
          style={{ marginBottom: 14 }}
          action={
            selectableItems.length > 1 ? (
              <span className="rod-hint">
                Pick the item you are handing over
              </span>
            ) : null
          }
        >
          <OrderItemsTable
            order={order}
            selectedItemId={itemId}
            onSelect={setItemId}
            selectable={!isRiderAssignmentLocked(order)}
            onOpenItem={(item) => {
              const listingId = itemKeyOf(item);
              const productId =
                item?.productId &&
                (typeof item.productId === "object"
                  ? item.productId._id
                  : item.productId);
              if (item?.productId && productId) {
                history.push(`/admin/brand-product-edit/${productId}`);
              } else if (listingId) {
                dispatch(getListing(listingId));
                history.push(`/admin/listing-details/${listingId}`);
              } else {
                toast.warn("Product details are not available for this line.");
              }
            }}
          />
        </Panel>

        {/* ── Fulfilment ───────────────────────────────────────────────── */}
        <Panel title="Fulfilment" style={{ marginBottom: 14 }}>
          {loadingDeliver && (
            <Loading
              size="inline"
              label="Saving..."
              style={{ marginBottom: 10, justifyContent: "flex-start" }}
            />
          )}

          <div className="rod-main-grid" style={{ marginBottom: 0 }}>
            <div>
              <AssignRiderPanel
                order={order}
                riders={riders}
                loadingRiders={loadingRiders}
                ridersError={ridersError}
                riderId={riderId}
                onRiderChange={setRiderId}
                selectedItemId={itemId}
                needsItemSelection
                onAssign={assignOrderToRider}
                assigning={loadingDeliver}
                currentRiderName={
                  order?.riderId?.firstName
                    ? `${order.riderId.firstName} ${
                        order.riderId.lastName || ""
                      }`.trim()
                    : null
                }
              />
            </div>

            <div>
              <div
                className="rod-fact-label"
                style={{ marginBottom: 8, display: "block" }}
              >
                Move it along
              </div>
              <div className="rod-actions">
                <button
                  type="button"
                  className="lk-action-btn lk-action-btn--lg"
                  disabled={!itemId || !canVendorDispatch(order)}
                  onClick={() => moveSelectedItemTo("DISPATCHED", "dispatched")}
                  title={
                    canVendorDispatch(order)
                      ? "The rider has the goods and is on the way"
                      : "Assign a rider first"
                  }
                >
                  Dispatch
                </button>
                <button
                  type="button"
                  className="lk-action-btn lk-action-btn--lg"
                  disabled={!itemId || !canVendorMarkDelivered(order)}
                  onClick={() => moveSelectedItemTo("DELIVERED", "delivered")}
                  title={
                    canVendorMarkDelivered(order)
                      ? "The customer has it"
                      : "Dispatch the order first"
                  }
                >
                  Mark delivered
                </button>
                {canCancel && (
                  <button
                    type="button"
                    className="lk-action-btn lk-action-btn--lg lk-action-btn--danger"
                    onClick={() => setShowCancelModal(true)}
                  >
                    Cancel order
                  </button>
                )}
              </div>

              {!itemId && selectableItems.length > 0 && (
                <div className="rod-hint" style={{ marginTop: 8 }}>
                  These act on the selected item. Choose one in the table above.
                </div>
              )}
              {allItemsClosed && (
                <div className="rod-hint" style={{ marginTop: 8 }}>
                  Every item on this order is finished.
                </div>
              )}
            </div>
          </div>
        </Panel>

        {order?._id && !order?.freight?.required && (
          <Panel title="Delivery fee adjustments" style={{ marginBottom: 14 }}>
            <div className="rod-hint" style={{ marginBottom: 12 }}>
              Pick what happened at dispatch; the server prices it. Nobody types
              an amount.
            </div>
            <DeliveryAdjustmentPanel orderId={order._id} />
          </Panel>
        )}

        {AUDIT_ENABLED && order?._id && (
          <Panel title="Order timeline">
            <div className="rod-hint" style={{ marginBottom: 12 }}>
              Every status change on this order — who, when, where, and how long
              each stage lasted. This log is immutable.
            </div>
            <OrderTimeline orderId={order._id} />
          </Panel>
        )}
      </div>

      {/* Mounted only while it is open. A react-bootstrap Modal builds a
          portal as soon as it renders, which keeps a hidden dialog in the
          tree and makes the screen impossible to render outside a browser —
          including in its own test. */}
      {showCancelModal && (
      <Modal show onHide={() => setShowCancelModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Cancel this order?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Every item in the customer's cart will be rejected. This cannot be
          undone from here.
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="lk-action-btn lk-action-btn--lg lk-action-btn--quiet"
            onClick={() => setShowCancelModal(false)}
          >
            Keep the order
          </button>
          <button
            type="button"
            className="lk-action-btn lk-action-btn--lg lk-action-btn--danger"
            onClick={handleCancelOrder}
          >
            Cancel order
          </button>
        </Modal.Footer>
      </Modal>
      )}
    </Container>
  );
};

export default OrderDetailsCard;
